import { useEffect, useState } from "react";
import { invoicesRepository } from "@/database/repositories/invoicesRepository";
import { Currencies, InvoicesHistoryData, TableFilterOption } from "@/types";
import { transactionsRepository } from "@/database/repositories/transactionsRepository";
import { $archivedInvoices, $blockRowNavigate, $customers, $invoices, $transactions } from "@/stores";
import { Flip, ToastContainer, toast } from 'react-toastify'
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    TableMeta,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import { invoicesColumns } from "@/components/history/InvoicesColumns";
import { DataTableToolbar } from "@/shared/table/TableToolbar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/table/TableElements";
import { DataTablePagination } from "@/shared/table/TablePagination";
import { customersRepository } from "@/database/repositories/customersRepository";
import { ArchiveRestore } from "lucide-react";
import { ConfirmDelete } from "@/shared/confirmDelete/ConfirmDelete";
import "./InvoiceHistory.scss";
import { useDb } from "@/database/provider/useDb";
import { Spinner } from "@/shared/spinner/Spinner";

export interface InvoicesTableMeta extends TableMeta<InvoicesHistoryData> {
    addTransaction: (amount: number, invoiceNumber: string, currency: Currencies, date: Date) => Promise<void>,
    archiveInvoice: (invoiceNumber: string) => Promise<void>
    deleteInvoice: (invoiceNumber: string) => Promise<void>
    openOptionsId: string | null,
    setOpenOptionsId: React.Dispatch<React.SetStateAction<string | null>>
}

const InvoiceHistory = () => {
    const db = useDb();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [filterBillTo, setFilterBillTo] = useState<TableFilterOption[]>([]);
    const [invoices, setInvoices] = useState<InvoicesHistoryData[]>([]);
    const [rowSelection, setRowSelection] = useState({});
    const [columnSizing, setColumnSizing] = useState({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [openOptionsId, setOpenOptionsId] = useState<string | null>(null);
    const [deleteManyOpen, setDeleteManyOpen] = useState(false);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [billToLoading, setBillToLoading] = useState(true);

    const addTransaction = async (amount: number, invoiceNumber: string, currency: Currencies, date: Date) => {
        try {
            await transactionsRepository.add(db, { amount, invoiceNumber, currency, date });
            await invoicesRepository.updateState(db, invoiceNumber);

            const invoices = await invoicesRepository.getAllUnArchived(db);
            setInvoices(invoices);
            $invoices.set({ isLoad: true, values: invoices });
            $transactions.set({ isLoad: true, values: await transactionsRepository.getAll(db) });
            toast.success('Transaction added!', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }
    const archiveMany = async () => {
        try {
            const ids = table.getSelectedRowModel().rows.map(row => row.original.invoiceNumber);
            await invoicesRepository.archiveMany(db, ids);

            const invoices = await invoicesRepository.getAllUnArchived(db);
            setInvoices(invoices);
            $invoices.set({ isLoad: true, values: invoices });
            $archivedInvoices.set({ isLoad: true, values: await invoicesRepository.getAllArchived(db) });
            setDeleteManyOpen(false);
            table.toggleAllRowsSelected(false);
            toast.success("Invoices archived!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
    }

    const archiveInvoice = async (invoiceNumber: string) => {
        try {
            await invoicesRepository.archive(db, invoiceNumber);

            const invoices = $invoices.value.values.filter((item) => item.invoiceNumber !== invoiceNumber);
            setInvoices(invoices);
            $invoices.set({ isLoad: true, values: invoices });
            $archivedInvoices.set({ isLoad: true, values: await invoicesRepository.getAllArchived(db) });

            toast.success(`Invoice ${invoiceNumber} archived!`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }
    const deleteInvoice = async (invoiceNumber: string) => {
        try {
            await invoicesRepository.delete(db, invoiceNumber);
            const invoices = $invoices.value.values.filter((item) => item.invoiceNumber !== invoiceNumber);
            setInvoices(invoices);
            $invoices.set({ isLoad: true, values: invoices });
            toast.success(`Invoice ${invoiceNumber} deleted!`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }

    const table = useReactTable({
        data: invoices,
        columns: invoicesColumns,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
            columnSizing
        },
        initialState: {
            sorting: [
                {
                    id: "invoiceNumber",
                    desc: true
                }
            ]
        },
        meta: {
            type: "Invoices",
            openOptionsId,
            setOpenOptionsId,
            addTransaction,
            archiveInvoice,
            deleteInvoice
        } as InvoicesTableMeta,
        enableRowSelection: true,
        onColumnSizingChange: setColumnSizing,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    useEffect(() => {
        try {
            if (!$invoices.value.isLoad) {
                invoicesRepository.getAllUnArchived(db).then((res) => {
                    setInvoices(res);
                    $invoices.set({ isLoad: true, values: res });
                });
            }
            else {
                setIsLoading(false);
                setInvoices($invoices.value.values);
            }

            if ($customers.value.isLoad) {
                setFilterBillTo($customers.value.values.map(({ name }) => {
                    return { value: name, text: `Customer ${name}` }
                }));
                setBillToLoading(false);
            }
            else customersRepository.getAll(db).then((res) => {
                setFilterBillTo(res.map(({ name }) => {
                    return { value: name, text: `Customer ${name}` }
                }));
                setBillToLoading(false)
            });
            const customersFilters = searchParams.get("billto");
            const statusesFilters = searchParams.get("status");

            if (customersFilters) {
                const customersArray = customersFilters.split(",");
                const column = table.getColumn("billTo");
                column?.setFilterValue(customersArray);
            }
            if (statusesFilters) {
                const statusesArray = statusesFilters.split(",");
                const column = table.getColumn("status");
                column?.setFilterValue(statusesArray);
            }
            const timeout = setTimeout(() => {
                setIsLoading(false);
            }, 600);
            return () => clearTimeout(timeout);
        }
        catch (err: any) {
            throw new Error(err.message);
        }
    }, []);

    return (
        <section className="history">
            <div className="history__container">
                <ToastContainer />
                {deleteManyOpen && <div className="overlay">
                    <ConfirmDelete
                        type="Invoice"
                        action="Archive"
                        count={table.getSelectedRowModel().rows.length}
                        onClose={() => { setDeleteManyOpen(false) }}
                        onDelete={archiveMany} />
                </div>}
                <div className="table-container">
                    <div className="table-container__data">
                        <div className="table-container__head">
                            <div className="table__head-container">
                                <h1>Invoices</h1>
                            </div>
                        </div>
                        {(!billToLoading && !isLoading) ? <><div className="table__toolbar-container">
                            <DataTableToolbar table={table}
                                filtersFields={[
                                    {
                                        title: "Status",
                                        field: "status", options: [
                                            {
                                                value: "paid",
                                                text: "Paid"
                                            },
                                            {
                                                value: "unpaid",
                                                text: "Unpaid"
                                            },
                                            {
                                                value: "partial-paid",
                                                text: "Partial paid"
                                            },
                                            {
                                                value: "overpaid",
                                                text: "Overpaid"
                                            }]
                                    },
                                    {
                                        field: "billTo",
                                        title: "Customer",
                                        options: filterBillTo
                                    }]}
                                searchField="title" />
                            {table.getSelectedRowModel().rows.length > 0 &&
                                <button className="table__delete-button" onClick={() => {
                                    setDeleteManyOpen(true);
                                }}>
                                    <ArchiveRestore width={14} height={14} /> Archive {table.getSelectedRowModel().rows.length} items
                                </button>}
                        </div>
                            <div className="table-container__table-area">
                                <Table>
                                    <TableHeader>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <TableRow key={headerGroup.id} >
                                                {headerGroup.headers.map((header) => {
                                                    return (
                                                        <TableHead key={header.id} style={{ width: `${header.getSize()}%` }}>
                                                            {header.isPlaceholder ? null
                                                                : flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                        </TableHead>
                                                    )
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableHeader>
                                    <TableBody>
                                        {table.getRowModel().rows?.length ? (
                                            table.getRowModel().rows.map((row) => (
                                                <TableRow
                                                    key={row.id}
                                                    className={`${row.getIsSelected() ? "selected-row" : ""} table__hover-row table__row`}
                                                    onClick={() => {
                                                        if (table.getSelectedRowModel().rows.length > 0) {
                                                            return row.toggleSelected()
                                                        }
                                                        if ($blockRowNavigate.value) return;
                                                        navigate({
                                                            pathname: "/preview",
                                                            search: `template=${row.original.invoiceNumber}`,
                                                        }, { state: { prevPath: `/history?${searchParams.toString()}` } });
                                                    }}
                                                >
                                                    {row.getVisibleCells().map((cell) => (
                                                        <TableCell key={cell.id} className="table__cell"
                                                            style={{ width: `${cell.column.getSize()}%` }}
                                                            onClick={(e) => {
                                                                const { id } = cell.column;
                                                                if (id === "select" || id === "actions") e.stopPropagation()
                                                            }}>
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))
                                        ) : (
                                            <div className="empty">
                                                <div className="empty__inner">
                                                    <img src="/Eyes.png" alt="No Data" />
                                                    <b>No results</b>
                                                </div>
                                            </div>)}
                                    </TableBody>
                                </Table>
                            </div>
                        </> : <div className="spinner-container">
                            <Spinner />
                        </div>}
                    </div>
                    <div className="table-container__pagination">
                        <DataTablePagination table={table} />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default InvoiceHistory;